import { FC } from 'react';
import { Button } from '@/components/ui';
import { Linkedin, Instagram, Facebook, Check, Twitter } from 'lucide-react';

export type SocialPlatform = 'linkedin' | 'instagram' | 'facebook' | 'meta' | 'twitter';

interface SocialConnectionCardProps {
    platform: SocialPlatform;
    isConnected: boolean;
    isLoading: boolean;
    connectedAccountName?: string;
    onConnect: () => void;
    onDisconnect: () => void;
    disabled?: boolean;
}

const platformConfig = {
    linkedin: {
        name: 'LinkedIn',
        icon: Linkedin,
        color: '#0A66C2',
        bgColor: 'bg-blue-50',
        borderColor: 'border-blue-200',
        textColor: 'text-blue-900',
        description: 'Post to your LinkedIn company page or profile',
    },
    instagram: {
        name: 'Instagram',
        icon: Instagram,
        color: '#E4405F',
        bgColor: 'bg-gradient-to-r from-purple-50 to-pink-50',
        borderColor: 'border-purple-200',
        textColor: 'text-purple-900',
        description: 'Post to your Instagram Business account',
    },
    facebook: {
        name: 'Facebook',
        icon: Facebook,
        color: '#1877F2',
        bgColor: 'bg-blue-50',
        borderColor: 'border-blue-200',
        textColor: 'text-blue-900',
        description: 'Post to your Facebook Page',
    },
    meta: {
        name: 'Meta (Facebook & Instagram)',
        icon: Facebook, // Using Facebook icon to represent Meta
        color: '#1877F2',
        bgColor: 'bg-gradient-to-r from-blue-50 to-purple-50',
        borderColor: 'border-blue-200',
        textColor: 'text-blue-900',
        description: 'Connect Facebook Pages and Instagram Business accounts',
    },
    twitter: {
        name: 'Twitter / X',
        icon: Twitter,
        color: '#000000',
        bgColor: 'bg-gray-50',
        borderColor: 'border-gray-200',
        textColor: 'text-gray-900',
        description: 'Post to your Twitter/X account',
    },
};

/**
 * SocialConnectionCard - Single responsibility component for one social platform
 * Displays connection status and provides connect/disconnect actions
 */
export const SocialConnectionCard: FC<SocialConnectionCardProps> = ({
    platform,
    isConnected,
    isLoading,
    connectedAccountName,
    onConnect,
    onDisconnect,
    disabled = false,
}) => {
    const config = platformConfig[platform];
    const Icon = config.icon;

    if (isConnected) {
        return (
            <div className="flex items-center justify-between p-3 bg-green-50 border border-green-200 rounded-lg">
                <div className="flex items-center gap-3">
                    <div className="p-2 bg-white rounded-lg shadow-sm">
                        <Icon className="w-5 h-5" style={{ color: config.color }} />
                    </div>
                    <div>
                        <div className="flex items-center gap-2">
                            <span className="text-sm font-medium text-gray-900">
                                {config.name}
                            </span>
                            <Check className="w-4 h-4 text-green-600" />
                        </div>
                        {connectedAccountName && (
                            <span className="text-xs text-gray-500">
                                {connectedAccountName}
                            </span>
                        )}
                    </div>
                </div>
                <Button
                    type="button"
                    variant="secondary"
                    size="sm"
                    onClick={onDisconnect}
                    isLoading={isLoading}
                    disabled={disabled}
                    className="text-xs"
                >
                    {isLoading ? 'Disconnecting...' : 'Disconnect'}
                </Button>
            </div>
        );
    }

    return (
        <div className={`p-3 ${config.bgColor} border ${config.borderColor} rounded-lg`}>
            <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                    <div className="p-2 bg-white rounded-lg shadow-sm">
                        <Icon className="w-5 h-5" style={{ color: config.color }} />
                    </div>
                    <div>
                        <span className={`text-sm font-medium ${config.textColor}`}>
                            {config.name}
                        </span>
                        <p className="text-xs text-gray-600 mt-0.5">
                            {config.description}
                        </p>
                    </div>
                </div>
                <Button
                    type="button"
                    variant="primary"
                    size="sm"
                    onClick={onConnect}
                    isLoading={isLoading}
                    disabled={disabled}
                    className="text-xs"
                    style={{ backgroundColor: config.color }}
                >
                    {isLoading ? 'Connecting...' : 'Connect'}
                </Button>
            </div>
        </div>
    );
};
